/**
 * OwnerPackagesSection — #1157 E2.2 (Ted #32b, his Item 32.5).
 *
 * Ted, 2026-10-06: a dog's package "is not showing in ALA, so it couldn't be
 * used at checkout." The pet profile showed nothing about packages at all, so
 * the one page the desk opens for a dog could not answer "does this visit
 * come out of a package?". Packages are the OWNER's (siblings draw from one
 * package), so this section shows the owner's balance on each of their dogs,
 * read-only, with the way to the owner record where History and Correct
 * balance live.
 *
 * Four honest states (DESIGN-PHILOSOPHY, five component states): loading,
 * a FAILED read ("couldn't load … check before charging" + Retry — never the
 * "No package on file" that sends the desk to charge a card), none on file,
 * and the balances. Rendered only for roles holding `payments-ops:read:all`;
 * the caller decides, so a floor role never collects a 403.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { PackageBalance } from '../checkout/PackageBalance.js';
import { useCustomerPackages } from '../checkout/useCustomerPackages.js';
import { ProfileSection } from './ProfileSection.js';

export interface OwnerPackagesSectionProps {
  readonly apiUrl: string;
  readonly owner: {
    readonly customerId: string;
    readonly firstName: string;
    readonly lastName: string;
  };
}

export function OwnerPackagesSection({ apiUrl, owner }: OwnerPackagesSectionProps): JSX.Element {
  const { load, reload } = useCustomerPackages(apiUrl, owner.customerId);
  const ownerName = `${owner.firstName} ${owner.lastName}`.trim() || 'The owner';
  const withDaysLeft = load.kind === 'ready' ? load.packages.filter((p) => p.remainingDays > 0) : [];
  const usedUp = load.kind === 'ready' ? load.packages.length - withDaysLeft.length : 0;

  let body: JSX.Element;
  if (load.kind === 'error') {
    body = (
      <PackageBalance
        pkg={null}
        variant="pet-card"
        emptyReason={{ kind: 'error', message: load.message, onRetry: () => void reload() }}
      />
    );
  } else if (load.kind !== 'ready') {
    body = <PackageBalance pkg={null} variant="pet-card" emptyReason={{ kind: 'loading' }} />;
  } else if (withDaysLeft.length === 0) {
    body =
      usedUp > 0 ? (
        <p className="ferro-dogprofile__empty" data-testid="dogprofile-packages-used-up">
          No package days left — {usedUp === 1 ? 'the package on file is' : `all ${usedUp} packages on file are`} used up.
        </p>
      ) : (
        <PackageBalance pkg={null} variant="pet-card" />
      );
  } else {
    body = (
      <div className="ferro-stack" data-testid="dogprofile-packages-list">
        {withDaysLeft.map((pkg) => (
          <div key={pkg.id} data-testid={`dogprofile-package-${pkg.id}`}>
            <PackageBalance pkg={pkg} variant="pet-card" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <ProfileSection
      title="Package balance"
      testId="dogprofile-packages"
      action={
        <Link
          className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
          to={`/customers/${owner.customerId}`}
          data-testid="dogprofile-packages-owner-link"
        >
          History &amp; corrections →
        </Link>
      }
    >
      <p className="ferro-dogprofile__note-text" data-testid="dogprofile-packages-owner">
        {ownerName}&rsquo;s packages, shared by all of their dogs.
      </p>
      {body}
    </ProfileSection>
  );
}
